import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CircleCheck, CircleAlert } from "lucide-react";
import axios from "axios";
import { createClient } from "@supabase/supabase-js";
import Topbar from "./Topbar";
import Footer from "./Footer";
import Loader from "./Loader";
const apiBase = process.env.REACT_APP_API_URL;
const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_KEY);

export default function TeamBuilderPage() {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [activeRoleFilter, setActiveRoleFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [league, setLeague] = useState(null);
  const [isFlexPick, setIsFlexPick] = useState(false);
  const [activeSlotIndex, setActiveSlotIndex] = useState(null);
  const [players, setPlayers] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [token, setToken] = useState("");
  // const budget = 540000;
  const [budget, setBudget] = useState(0);
  const MAX_PLAYERS = 6;
  const [hasExistingTeam, setHasExistingTeam] = useState(false);

  useEffect(() => {
    const fetchTeamBuilderData = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) {
          window.location.href = "/login";
          return;
        }

        const accessToken = session.access_token;
        setToken(accessToken);

        const res = await axios.get(`${apiBase}/api/league/${leagueId}/team-builder`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        const data = res.data;
        if (!data.success) throw new Error(data.error);

        setLeague(data.league);
        setPlayers(data.availablePlayers || []);
        setSelectedPlayers(data.teamPlayers || []);
        setBudget(data.startingBudget ?? 540000);
        setHasExistingTeam((data.teamPlayers || []).length > 0);
        if ((data.teamPlayers || []).length > 0) {
          navigate(`/dashboard/league/${leagueId}`);
          return;
        }
      } catch (err) {
        console.error("Failed to load team builder:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamBuilderData();
  }, [leagueId, navigate]);

  const getTotalCost = () => selectedPlayers.reduce((total, p) => total + (p.players?.price || 0), 0);

  const remainingBudget = budget - getTotalCost();
  const isOverBudget = remainingBudget < 0;
  const isLineupFull = selectedPlayers.length === 6;

  const canSubmit = isLineupFull && !isOverBudget;

  const handleSelectPlayer = (p) => {
    if (selectedPlayers.find((s) => s.player_id === p.id)) return null;

    if (selectedPlayers.length >= 6) {
      return null;
    }

    setSelectedPlayers((prev) => [
      ...prev,
      {
        player_id: p.id,
        players: p,
        is_starter: !isFlexPick,
        slot_index: activeSlotIndex,
      },
    ]);

    return p.id;
  };

  const validateTeam = () => {
    if (selectedPlayers.length > 6) {
      alert("You can only select 6 total players.");
      return;
    }

    // Required roles for a full lineup
    const roles = ["Top", "Jng", "Mid", "Bot", "Sup"];

    // Count occurrences of roles in the selected players
    const selectedRoles = selectedPlayers.map((p) => p.players?.role);

    const hasAllRoles = roles.every((role) => selectedRoles.includes(role));

    if (!hasAllRoles) {
      alert("Your team must include 1 Top, 1 Jungle, 1 Mid, 1 ADC, and 1 Support. The 6th can be any role.");
      return false;
    }

    if (getTotalCost() > budget) {
      alert("Team exceeds the budget!");
      return false;
    }

    return true;
  };

  const handleSaveTeam = async () => {
    if (!validateTeam()) return;

    try {
      setSaving(true);

      const res = await axios.post(
        `${apiBase}/api/league/${leagueId}/save-team`,
        {
          selectedPlayers: selectedPlayers.map((p) => ({
            player_id: p.player_id,
            boost_id: "no_boost",
            is_starter: p.is_starter,
          })),
          remaining_budget: remainingBudget,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.data.success) {
        navigate(`/dashboard/league/${leagueId}`);
      } else {
        alert("❌ Failed to save team: " + res.data.error);
      }
    } catch (err) {
      console.error("Error saving team:", err);
      alert("❌ There was an error saving your team.");
    } finally {
      setSaving(false);
    }
  };

  const setBenchPlayer = (playerId) => {
    setSelectedPlayers((prev) =>
      prev.map((p) => ({
        ...p,
        is_starter: p.player_id === playerId ? false : true,
      })),
    );
  };

  const formatBudget = (n) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toFixed(0);
  };

  const selectedCount = selectedPlayers.length;
  const requiredRoles = ["Top", "Jng", "Mid", "Bot", "Sup"];
  const SLOT_ORDER = ["Top", "Jng", "Mid", "Bot", "Sup", "Flex"];
  const selectedRoles = selectedPlayers.map((p) => p.players?.role);
  const roleChecklist = requiredRoles.map((role) => ({
    role,
    filled: selectedRoles.includes(role),
  }));

  const modalPlayers = players
    .filter((p) => !selectedPlayers.some((sp) => sp.player_id === p.id) && (!activeRoleFilter || p.role === activeRoleFilter))
    .sort((a, b) => b.price - a.price);

  if (loading) return <Loader loadingTitle={"Loading Team Builder"} />;
  if (!league) return <p style={{ color: "white" }}>League not found.</p>;
  if (hasExistingTeam) {
    return <p style={{ color: "white" }}>You already built your team for this league.</p>;
  }

  return (
    <div className="page-container">
      <Topbar showBudget={false} showLinks={false} />
      <div className="main" style={{ paddingBottom: "60px" }}>
        <h1 style={{ fontStyle: "italic", fontWeight: "800", color: "white", letterSpacing: "-0.4px" }} className="tournament-stage-title">
          <img src={`/banners/${league.banner}`} alt={league.name} style={{ height: "28px", marginRight: "10px" }} />
          {league.name}
        </h1>
        <p style={{ color: "rgb(156, 163, 175)", marginBottom: "30px", fontSize: "14px", marginTop: "0" }}>
          Build your fantasy team for the {league.region} region
        </p>
        <div
          style={{
            marginTop: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "20px",
          }}>
          {/* Role Checklist */}
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginRight: "14px" }}>
              <span style={{ color: remainingBudget < 0 ? "red" : "#00ff90", fontWeight: "800", display: "flex", alignItems: "center", gap: "4px" }}>
                ${formatBudget(remainingBudget)} / $
                {formatBudget(budget)}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px" }}>
              {roleChecklist.map(({ role, filled }) => (
                <div
                  key={role}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "13px",
                    padding: "4px 12px",
                    borderRadius: "80px",
                    background: filled ? "rgba(0,255,144,0.15)" : "rgba(0,0,0,0.4)",
                    border: `1px solid ${filled ? "#00ff90" : "rgba(255,255,255,0.2)"}`,
                    color: filled ? "#00ff90" : "#ccc",
                  }}>
                  {filled ? <CircleCheck size={16} /> : "○"} {role}
                </div>
              ))}
              {/* Flex Slot */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "13px",
                  padding: "4px 12px",
                  borderRadius: "8px",
                  background:
                    selectedCount === MAX_PLAYERS && roleChecklist.every((r) => r.filled) ? "rgba(0,255,144,0.15)" : "rgba(255,255,255,0.08)",
                  border: "1px dashed rgba(255,255,255,0.25)",
                  color: "#aaa",
                }}>
                Bench
              </div>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "700",
                  padding: "4px 12px",
                  borderRadius: "80px",
                  background: "rgba(0,0,0,0.4)",
                  border: `1px solid rgba(255,255,255,0.2)`,
                  color: "white",
                }}>
                {selectedCount} / {MAX_PLAYERS}
              </span>
            </div>
          </div>
        </div>
        {isOverBudget && (
          <div
            style={{
              color: "white",
              background: "rgb(234 57 67 / 15%)",
              border: "1px solid #ea3943",
              borderRadius: "8px",
              padding: "14px 16px",
              textAlign: "center",
              marginTop: "20px",
              fontWeight: "700",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: 10,
              justifyContent: "center",
            }}>
            <CircleAlert size={18} /> OVER BUDGET BY ${Math.abs(remainingBudget).toLocaleString()}
          </div>
        )}
        <div style={{ display: "flex", gap: "40px", marginTop: "30px" }}>
          {/* Selected Players */}
          <div style={{ flex: 1 }}>
            <div className="team-builder-grid">
              {SLOT_ORDER.map((slotRole, slotIndex) => {
                const sp = selectedPlayers.find((p) => p.slot_index === slotIndex);
                const isFlex = slotRole === "Flex";

                // ───────────── EMPTY SLOT ─────────────
                if (!sp) {
                  return (
                    <div
                      key={`slot-${slotIndex}`}
                      onClick={() => {
                        setIsFlexPick(isFlex);
                        setActiveRoleFilter(isFlex ? null : slotRole);
                        setActiveSlotIndex(slotIndex);
                        setRoleModalOpen(true);
                      }}
                      style={{
                        cursor: "pointer",
                        borderRadius: "8px",
                        padding: "10px",
                        textAlign: "center",
                        border: `2px dashed ${isFlex ? "rgba(255,255,255,0.25)" : "#3273fa"}`,
                        background: isFlex ? "rgba(255,255,255,0.05)" : "#3273fa1a",
                        color: isFlex ? "rgba(255,255,255,0.6)" : "white",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "160px",
                      }}>
                      <div style={{ fontSize: "11px", opacity: 0.8 }}>{isFlex ? "CHOICE ROLE" : "REQUIRED ROLE"}</div>

                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          textTransform: "uppercase",
                        }}>
                        {isFlex ? (
                          "Bench Player"
                        ) : (
                          <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            Needs
                            <img src={`/roles/${slotRole}.svg`} alt={slotRole} style={{ width: "18px", height: "18px" }} />
                          </span>
                        )}
                      </div>

                      <div style={{ marginTop: "6px", fontSize: "22px", opacity: 0.7 }}>＋</div>
                    </div>
                  );
                }

                // ───────────── FILLED SLOT ─────────────
                return (
                  <div
                    key={sp.player_id}
                    style={{
                      background: `linear-gradient(
  130deg,
  rgba(${sp.players?.teams.color}, 0.3),
  rgba(0, 255, 144, 0) 70%
)`,
                      display: "flex",
                      position: "relative",
                      overflow: "hidden",
                      padding: "10px 15px",
                      borderRadius: "8px",
                      zIndex: "90",
                      boxShadow:
                        " rgba(0, 0, 0, 0.4) 0px -4px 8px -2px inset, rgba(236, 233, 233, 0.2) -1px 3px 6px -2px inset,  rgba(0, 0, 0, 0.2) 0px 30px 20px -30px",
                      boxSizing: "border-box",
                      minWidth: "200px",
                      width: "100%",
                      cursor: "pointer",
                      alignItems: "center",
                    }}>
                    <button
                      onClick={() => setSelectedPlayers((prev) => prev.filter((p) => p.player_id !== sp.player_id))}
                      style={{
                        position: "absolute",
                        top: "5px",
                        right: "5px",
                        fontSize: "20px",
                        fontWeight: 700,
                        background: "transparent",
                        color: "white",
                        border: "none",
                        cursor: "pointer",
                      }}>
                      ✕
                    </button>
                    <img
                      src={`/teams/${sp.players?.teams.team_logo}`}
                      alt={sp.players?.teams.name}
                      style={{
                        position: "absolute",
                        height: "200px",
                        width: "200px",
                        objectFit: "contain",
                        opacity: "0.1",
                        right: "-30px",
                        bottom: "-40px",
                        zIndex: "-1",
                      }}
                    />
                    <img
                      src={`/players/${sp.players?.player_image}`}
                      alt={sp.players?.name}
                      style={{ height: "150px", objectFit: "cover", zIndex: "90" }}
                    />
                    <div style={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between" }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          marginRight: "20px",
                          marginLeft: "10px",
                          justifyContent: "center",
                          gap: "4px",
                        }}>
                        <p style={{ margin: 0, opacity: 0.6, fontSize: "12px", color: "white" }}>{sp.players?.role.toUpperCase()}</p>
                        <p style={{ margin: 0, fontWeight: "800", fontStyle: "italic", color: "white" }}>{sp.players?.name.toUpperCase()}</p>
                        <p style={{ margin: 0, opacity: 0.6, fontSize: "12px", color: "white" }}>{sp.players?.teams?.name.toUpperCase()}</p>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "14px",
                            color: "#00ff90",
                            display: "flex",
                            alignItems: " center",
                            gap: "4px",
                            fontWeight: "800",
                          }}>
                          ${formatBudget(sp.players?.price)}
                        </p>
                      </div>
                      <img src={`/roles/${sp.players?.role}.svg`} className="role-icon" alt={`${sp.players?.role}`} />
                    </div>
                    {!sp.is_starter && (
                      <div
                        style={{
                          position: "absolute",
                          top: "6px",
                          left: "6px",
                          background: "#ff9800",
                          color: "black",
                          fontSize: "10px",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontWeight: "bold",
                          zIndex: "90",
                        }}>
                        BENCH
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {roleModalOpen && (
          <div className="modal-overlay" onClick={() => setRoleModalOpen(false)}>
            <div onClick={(e) => e.stopPropagation()} className="modal" style={{ overflow: "auto", width: "100%" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "6px", marginRight: "14px", flexDirection: "column" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <h2 style={{ margin: 0 }}>{activeRoleFilter ? `SELECT ${activeRoleFilter.toUpperCase()}` : "Select Player"}</h2>
                    <img src={`/roles/${activeRoleFilter}.svg`} style={{ width: "18px", height: "18px" }} alt={activeRoleFilter} />
                  </div>
                  <span
                    style={{ color: remainingBudget < 0 ? "red" : "#00ff90", fontWeight: "800", display: "flex", alignItems: "center", gap: "4px" }}>
                    ${formatBudget(remainingBudget)} / $
                    {formatBudget(budget)}
                  </span>
                </div>
                <button
                  onClick={() => setRoleModalOpen(false)}
                  style={{
                    background: "transparent",
                    color: "white",
                    border: "none",
                    fontSize: "20px",
                    cursor: "pointer",
                    zIndex: "100",
                  }}>
                  ✕
                </button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                  rowGap: "20px",
                  columnGap: "10px",
                }}>
                {modalPlayers.map((p) => (
                  <div
                    key={p.id}
                    onClick={() => {
                      const newPlayerId = handleSelectPlayer(p);

                      if (newPlayerId && isFlexPick) {
                        // Let React add the player first
                        setTimeout(() => setBenchPlayer(newPlayerId), 0);
                      }

                      setRoleModalOpen(false);
                    }}
                    style={{
                      background: `linear-gradient(
  130deg,
  rgba(${p.teams.color}, 0.3),
  rgba(0, 255, 144, 0) 70%
)`,
                      display: "flex",
                      position: "relative",
                      overflow: "hidden",
                      padding: "10px 15px",
                      borderRadius: "8px",
                      boxShadow:
                        " rgba(0, 0, 0, 0.4) 0px -4px 8px -2px inset, rgba(236, 233, 233, 0.2) -1px 3px 6px -2px inset,  rgba(0, 0, 0, 0.2) 0px 30px 20px -30px",
                      boxSizing: "border-box",
                      minWidth: "200px",
                      width: "100%",
                      cursor: "pointer",
                    }}>
                    <img
                      src={`/teams/${p.teams.team_logo}`}
                      alt={p.teams.name}
                      style={{
                        position: "absolute",
                        height: "120px",
                        width: "120px",
                        objectFit: "contain",
                        opacity: "0.3",
                        left: "-20px",
                        top: "-12px",
                      }}
                    />
                    <img
                      src={`/players/${p.player_image}`}
                      alt={p.name}
                      style={{ height: "108px", objectFit: "cover", marginLeft: "20px", zIndex: "90" }}
                    />
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        marginRight: "20px",
                        marginLeft: "10px",
                        justifyContent: "center",
                        gap: "4px",
                      }}>
                      <p style={{ margin: 0, opacity: 0.6, fontSize: "12px", color: "white" }}>{p.role.toUpperCase()}</p>
                      <p style={{ margin: 0, fontWeight: "800", fontStyle: "italic", color: "white" }}>{p.name.toUpperCase()}</p>
                      <p style={{ margin: 0, opacity: 0.6, fontSize: "12px", color: "white" }}>{p?.teams?.name.toUpperCase()}</p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "14px",
                          color: "#00ff90",
                          display: "flex",
                          alignItems: " center",
                          gap: "4px",
                          fontWeight: "800",
                        }}>
                        ${formatBudget(p.price)}
                      </p>
                    </div>
                    {p.is_sub && (
                      <span
                        style={{
                          background: "#a855f7",
                          color: "white",
                          fontSize: "10px",
                          position: "absolute",
                          top: "0",
                          right: "0",
                          padding: "2px 8px",
                          fontWeight: "bold",
                          width: "fit-content",
                        }}>
                       [Sub] Limited Play
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {modalPlayers.length === 0 && (
                <p style={{ opacity: 0.7, marginTop: "20px", textAlign: "center" }}>No available players for this role.</p>
              )}
            </div>
          </div>
        )}

        {/* Save Team Button */}
        <div style={{ marginTop: "40px", textAlign: "center" }}>
          <button
            disabled={saving || !canSubmit}
            style={{
              cursor: saving ? "not-allowed" : "pointer",
              width: "100%",
            }}
            className="edit-btn"
            onClick={handleSaveTeam}>
            {saving ? "Saving..." : "Submit Team"}
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
